import type { AidePage } from '@oasis/contracts';
import { KeyRound, Link2, Settings2 } from 'lucide-react';
import type { ReactElement } from 'react';
import { Link } from 'react-router';

import { Markdown } from './Markdown.js';
import { ESPACE_LIBELLE } from './logique.js';

/**
 * Contenu d'une page d'aide, commun au panneau et au centre d'aide : texte orienté tâches
 * écrit avec le module, puis ce que le serveur a déduit des déclarations (droits et profils
 * qui les donnent, écrans liés, réglages modifiables).
 */
export function AideContenu({
  page,
  titre = 'h2',
}: {
  page: AidePage;
  titre?: 'h1' | 'h2';
}): ReactElement {
  const Titre = titre;
  return (
    <article className="o-aide-page" aria-labelledby={`aide-${page.id}`}>
      <header className="o-aide-page__entete">
        <Titre id={`aide-${page.id}`}>{page.titre}</Titre>
        <p className="o-hint">
          {page.moduleLibelle} · {page.espaces.map((e) => ESPACE_LIBELLE[e]).join(', ')}
        </p>
      </header>
      <Markdown source={page.corps} decalage={titre === 'h1' ? 1 : 2} />

      {page.droits.length > 0 && (
        <section className="o-aide-section">
          <h3>
            <KeyRound aria-hidden="true" size={16} /> Droits nécessaires
          </h3>
          <ul>
            {page.droits.map((d) => (
              <li key={d.key}>
                <strong>{d.label}</strong>
                {d.sensible && <span className="o-badge o-badge--warning">sensible</span>}
                <br />
                <span className="o-hint">
                  {d.profils.length > 0
                    ? `Donné par : ${d.profils.join(', ')}`
                    : 'Donné par une attribution directe (aucun profil ne le contient).'}
                </span>
              </li>
            ))}
          </ul>
        </section>
      )}

      {page.liens.length > 0 && (
        <section className="o-aide-section">
          <h3>
            <Link2 aria-hidden="true" size={16} /> Écrans liés
          </h3>
          <ul className="o-aide-liens">
            {page.liens.map((l) => (
              <li key={l.path}>
                <Link to={l.path}>{l.label}</Link> <span className="o-hint">({l.section})</span>
              </li>
            ))}
          </ul>
        </section>
      )}

      {page.reglages.length > 0 && (
        <section className="o-aide-section">
          <h3>
            <Settings2 aria-hidden="true" size={16} /> Réglages modifiables
          </h3>
          <ul>
            {page.reglages.map((r) => (
              <li key={r.key}>
                <code>{r.key}</code> — {r.description}
              </li>
            ))}
          </ul>
        </section>
      )}
    </article>
  );
}
